import { createContext, useContext, type ReactNode } from "react";
import { useQueryStates, createParser } from "nuqs";
import { format, parse, isValid } from "date-fns";

interface DateRange {
  startDate: Date;
  endDate: Date;
}

interface DateRangeContextType {
  dateRange: DateRange;
  setDateRange: (range: DateRange) => void;
  formatForApi: () => { startDate: string; endDate: string };
  formatForDisplay: () => string;
}

const DateRangeContext = createContext<DateRangeContextType | null>(null);

// Custom date parser that treats dates as LOCAL time (not UTC)
const parseAsLocalDate = createParser({
  parse: (value: string) => {
    // Parse as local date by using date-fns parse with a reference date
    const parsed = parse(value, "yyyy-MM-dd", new Date());
    return isValid(parsed) ? parsed : null;
  },
  serialize: (date: Date) => {
    return format(date, "yyyy-MM-dd");
  },
});

// Default dates - All Time (from year 2000 to today)
const defaultStartDate = new Date(2000, 0, 1);
const defaultEndDate = new Date();

export function DateRangeProvider({ children }: { children: ReactNode }) {
  // Use nuqs to sync date range with URL query params
  const [queryDates, setQueryDates] = useQueryStates(
    {
      startDate: parseAsLocalDate.withDefault(defaultStartDate),
      endDate: parseAsLocalDate.withDefault(defaultEndDate),
    },
    {
      history: "push",
    }
  );

  const dateRange: DateRange = {
    startDate: queryDates.startDate,
    endDate: queryDates.endDate,
  };

  const setDateRange = (range: DateRange) => {
    setQueryDates({
      startDate: range.startDate,
      endDate: range.endDate,
    });
  };

  const formatForApi = () => ({
    startDate: format(dateRange.startDate, "yyyy-MM-dd"),
    endDate: format(dateRange.endDate, "yyyy-MM-dd"),
  });

  const formatForDisplay = () => {
    // Show "All Time" if start date is Jan 1, 2000 or earlier
    if (dateRange.startDate.getFullYear() <= 2000) {
      return "All Time";
    }
    return `${format(dateRange.startDate, "MMM d, yyyy")} - ${format(dateRange.endDate, "MMM d, yyyy")}`;
  };

  return (
    <DateRangeContext.Provider value={{ dateRange, setDateRange, formatForApi, formatForDisplay }}>
      {children}
    </DateRangeContext.Provider>
  );
}

export function useDateRange() {
  const context = useContext(DateRangeContext);
  if (!context) {
    throw new Error("useDateRange must be used within a DateRangeProvider");
  }
  return context;
}
